import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { fetchContent } from "@/lib/api";
import { MaskedLines, FadeUp, RevealImage } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { AlpanaCorner, KanthaStitch, Fish, PaisleyMotif } from "@/components/Motifs";

const FALLBACK = {
  hero_image: "/images/hero.jpg",
  hero_kicker: "Fashion Designer • Bengali Folk Artist",
  hero_line1: "Threads of Bengal.",
  hero_line2: "Stories in Form.",
  hero_sub: "A contemporary fashion practice rooted in Bengal's folk traditions, craftsmanship and visual storytelling.",
  intro_line1: "Fashion becomes folklore.",
  intro_line2: "Folklore becomes form.",
  intro_text: "Ipsita explores fashion as a canvas for memory, folklore and craftsmanship — translating the visual language of Bengal into contemporary silhouettes, textiles and artworks.",
  intro_image: "https://images.unsplash.com/photo-1633887091273-a3bd71efddde?auto=format&fit=crop&w=1200&q=80",
  quote_text: "I do not separate fashion from art. For me, a garment can carry memory, mythology and place.",
  quote_author: "Ipsita",
  about_preview_title: "Artist. Designer. Storyteller.",
  about_preview_text: "Ipsita's creative practice exists between fashion, Bengali folk culture, illustration and craftsmanship — a single universe where a sari border can begin as a painting, and a painting can end as a silhouette.",
  about_preview_image: "/images/about-portrait.jpg",
  editorial_label: "Editorial 01",
  editorial_title: "The Woman in Red",
  editorial_text: "A visual study of Bengal, femininity and ritual through contemporary fashion.",
  editorial_image: "/images/editorial.jpg",
  editorial_link: "/journal/the-woman-in-red",
  work_categories: ["Custom Fashion", "Art Commissions", "Brand Collaborations", "Editorial Styling", "Textile / Surface Design", "Cultural Projects", "Exhibitions", "Workshops"],
  studio_images: [],
  contact_note: "Every enquiry is read personally. Expect a reply within three working days.",
};

const PROCESS = [
  { n: "01", title: "Research", text: "Bengali folklore, rituals, craft traditions and visual history." },
  { n: "02", title: "Drawing", text: "Motifs and stories begin as handmade sketches." },
  { n: "03", title: "Textile", text: "Illustration moves onto fabric through embroidery, print, dye or surface work." },
  { n: "04", title: "Form", text: "The artwork becomes a contemporary garment." },
  { n: "05", title: "Story", text: "The finished piece carries its original cultural narrative." },
];

const CRAFT_IMAGES = [
  "https://images.unsplash.com/photo-1528459801416-a9e53bbf4e17?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1566152467605-fd2f144798e5?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1609881583302-61548332039c?auto=format&fit=crop&w=900&q=80",
  "/images/studio-alpana.jpg",
];

export default function Home() {
  const [c, setC] = useState(FALLBACK);
  const [collections, setCollections] = useState([]);
  const [artworks, setArtworks] = useState([]);

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 900], [0, 220]);
  const heroOpacity = useTransform(scrollY, [0, 700], [1, 0.35]);

  useEffect(() => {
    fetchContent("/content").then((data) => {
      const merged = { ...FALLBACK, ...data };
      setC(merged);
      window.__siteContent = merged;
    });
    fetchContent("/collections").then(setCollections);
    fetchContent("/artworks").then(setArtworks);
  }, []);

  return (
    <main data-testid="home-page">
      {/* ---------- HERO ---------- */}
      <section className="relative hero-svh overflow-hidden bg-ink" data-testid="hero-section">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img
            src={c.hero_image}
            alt="Bengali-inspired contemporary fashion editorial"
            className="w-full h-[115%] object-cover hero-breathe"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/25 to-ink/15" />

        <motion.div style={{ opacity: heroOpacity }} className="absolute inset-0 flex flex-col justify-end">
          <div className="max-w-[1500px] mx-auto w-full px-5 md:px-10 pb-16 md:pb-24 relative">
            <AlpanaCorner className="absolute -top-40 right-4 md:right-16 w-28 h-28 md:w-40 md:h-40 text-paper/30 rotate-180" />
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.9 }}
              data-testid="hero-kicker"
              className="text-paper/85 text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.35em] uppercase mb-5 max-w-md"
            >
              IPSITA — {c.hero_kicker}
            </motion.p>
            <MaskedLines
              lines={[c.hero_line1, c.hero_line2]}
              delay={0.65}
              className="font-serif font-light text-paper text-5xl sm:text-6xl lg:text-8xl leading-[1.02] tracking-tight"
              lineClassName="last:italic last:text-paper"
            />
            <motion.p
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.9 }}
              className="mt-6 max-w-xl text-paper/80 font-light text-sm md:text-base leading-relaxed"
            >
              {c.hero_sub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.35, duration: 0.9 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link
                to="/collections"
                data-testid="hero-explore-button"
                className="inline-flex items-center bg-alta text-paper px-8 py-3.5 text-[11px] font-medium tracking-[0.22em] uppercase transition-all duration-500 hover:bg-paper hover:text-ink"
              >
                Explore the Work
              </Link>
              <Link
                to="/about"
                data-testid="hero-artist-button"
                className="inline-flex items-center border border-paper/60 text-paper px-8 py-3.5 text-[11px] font-medium tracking-[0.22em] uppercase transition-all duration-500 hover:bg-paper hover:text-ink"
              >
                Meet the Artist
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-6 right-6 md:right-10 hidden md:flex flex-col items-center gap-3"
          aria-hidden="true"
        >
          <span className="text-paper/60 text-[10px] tracking-[0.3em] uppercase rotate-90 origin-center translate-y-[-8px]">Scroll</span>
          <motion.span
            className="w-px h-14 bg-paper/50 block"
            animate={{ scaleY: [0.3, 1, 0.3] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: "top" }}
          />
        </motion.div>
      </section>

      {/* ---------- MARQUEE ---------- */}
      <Marquee
        items={["LAAL", "MATI", "NODI", "KATHA", "সূতো", "কথা", "Threads of Bengal", "রঙ", "শিল্প", "ঐতিহ্য"]}
        className="bg-paper text-ink"
      />

      {/* ---------- ARTISTIC INTRO ---------- */}
      <section className="relative py-28 md:py-40 overflow-hidden" data-testid="intro-section">
        <span className="absolute top-10 right-2 md:right-10 font-bengali text-[22vw] leading-none text-terracotta/[0.06] select-none pointer-events-none" aria-hidden="true">শিল্প</span>
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7">
            <FadeUp>
              <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-8">The Practice</p>
              <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink">
                {c.intro_line1}
                <br />
                <em className="text-terracotta">{c.intro_line2}</em>
              </h2>
              <p className="mt-10 max-w-lg text-base md:text-lg font-light leading-relaxed text-ink/75">
                {c.intro_text}
              </p>
              <Link to="/about" data-testid="intro-story-link" className="link-underline inline-block mt-10 text-[11px] font-medium tracking-[0.25em] uppercase text-ink">
                Read the Story
              </Link>
            </FadeUp>
          </div>
          <div className="md:col-span-5 relative">
            <RevealImage src={c.intro_image} alt="Ipsita in her studio" className="aspect-[3/4] kantha-border p-2 bg-bone" cursor="VIEW" imgClassName="kantha-border" />
            <FadeUp delay={0.25} className="absolute -bottom-6 -left-6 bg-ink text-paper px-6 py-4">
              <p className="font-bengali text-xl text-paper/90">বাংলা</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-paper/50 mt-1">The source</p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ---------- SELECTED COLLECTIONS ---------- */}
      <section id="selected-collections" className="py-28 md:py-36 bg-bone relative" data-testid="collections-section">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <FadeUp className="flex items-end justify-between mb-16 md:mb-20">
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-5">Selected Collections</p>
              <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">Chapters in Cloth</h2>
            </div>
            <Link to="/collections" data-testid="collections-archive-link" className="link-underline hidden md:inline-block text-[11px] font-medium tracking-[0.25em] uppercase text-ink">
              Full Archive
            </Link>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12">
            {collections.map((col, i) => (
              <FadeUp
                key={col.id}
                delay={(i % 2) * 0.12}
                className={i % 2 === 0 ? "md:col-span-5" : "md:col-span-7 md:mt-24"}
              >
                <Link to={`/collections/${col.slug}`} data-testid={`collection-card-${col.slug}`} className="group block" data-cursor="EXPLORE">
                  <RevealImage
                    src={col.hero_image}
                    alt={col.title}
                    className={i % 2 === 0 ? "aspect-[3/4]" : "aspect-[4/3]"}
                  />
                  <div className="mt-6 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-serif text-3xl md:text-4xl font-light tracking-wide text-ink group-hover:text-alta transition-colors duration-500">
                        {col.title}
                      </h3>
                      <p className="mt-2 text-sm font-light text-ink/60 max-w-md">{col.concept}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[11px] tracking-[0.25em] text-ink/45">{col.year}</p>
                      <p className="link-underline mt-3 text-[10px] font-medium tracking-[0.22em] uppercase text-terracotta">
                        View Collection
                      </p>
                    </div>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- PHILOSOPHY QUOTE ---------- */}
      <section className="relative py-32 md:py-44 bg-linen overflow-hidden" data-testid="philosophy-section">
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-bengali text-[26vw] leading-none text-terracotta/[0.07] select-none pointer-events-none" aria-hidden="true">কথা</span>
        <div className="relative max-w-4xl mx-auto px-5 md:px-10 text-center">
          <FadeUp>
            <Fish className="w-10 h-10 mx-auto mb-10 text-terracotta/60" />
            <blockquote className="font-serif font-light italic text-3xl sm:text-4xl lg:text-5xl leading-[1.25] text-walnut">
              “{c.quote_text}”
            </blockquote>
            <p className="mt-10 text-[11px] tracking-[0.35em] uppercase text-maroon">— {c.quote_author}</p>
            <KanthaStitch className="mt-12 max-w-xs mx-auto" />
          </FadeUp>
        </div>
      </section>

      {/* ---------- FOLK ARTIST TEASER ---------- */}
      <section className="py-28 md:py-36" data-testid="folk-artist-section">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4 flex flex-col justify-center">
              <FadeUp>
                <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-5">Beyond the Garment</p>
                <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-ink">The Folk Artist</h2>
                <p className="mt-8 text-base font-light leading-relaxed text-ink/70">
                  Independent of the runway, Ipsita paints, stitches and draws — patachitra-inspired works, alpana studies and textile art that form the living archive behind every collection.
                </p>
                <Link to="/folk-art" data-testid="folk-gallery-link" className="link-underline mt-10 inline-block text-[11px] font-medium tracking-[0.25em] uppercase text-ink">
                  Enter the Gallery
                </Link>
              </FadeUp>
            </div>
            <div className="md:col-span-8 columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
              {artworks.slice(0, 5).map((a, i) => (
                <FadeUp key={a.id} delay={i * 0.08} className="mb-5 break-inside-avoid">
                  <Link to="/folk-art" data-cursor="VIEW" data-testid={`folk-teaser-${a.id}`}>
                    <RevealImage src={a.image} alt={a.title} className={i % 3 === 0 ? "aspect-[3/4]" : i % 3 === 1 ? "aspect-square" : "aspect-[4/5]"} />
                    <p className="mt-3 font-serif text-lg text-ink">{a.title}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-ink/45 mt-1">{a.category}</p>
                  </Link>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- FROM STORY TO SILHOUETTE ---------- */}
      <section className="py-28 md:py-40 bg-paper relative overflow-hidden" data-testid="process-section">
        <span className="absolute bottom-0 left-0 font-bengali text-[18vw] leading-none text-terracotta/[0.05] select-none pointer-events-none" aria-hidden="true">সূতো</span>
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <FadeUp className="mb-20">
            <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-5">The Process</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">From Story to Silhouette</h2>
          </FadeUp>
          <div>
            {PROCESS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.06}>
                <div
                  data-testid={`process-step-${s.n}`}
                  className={`group flex flex-col md:flex-row md:items-baseline gap-3 md:gap-10 py-9 border-t border-terracotta/20 ${i % 2 === 1 ? "md:pl-24" : ""}`}
                >
                  <span className="font-serif text-5xl md:text-6xl font-light text-alta/80 group-hover:text-alta transition-colors duration-500 shrink-0 w-24">
                    {s.n}
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl text-ink">{s.title}</h3>
                    <p className="mt-2 text-sm md:text-base font-light text-ink/65 max-w-lg">{s.text}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
            <KanthaStitch />
          </div>
        </div>
      </section>

      {/* ---------- FEATURED EDITORIAL ---------- */}
      <section className="relative h-[85vh] overflow-hidden" data-testid="featured-editorial">
        <RevealImage src={c.editorial_image} alt={c.editorial_title} className="absolute inset-0 h-full" imgClassName="slow-zoom" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/75 via-ink/30 to-transparent" />
        <div className="relative h-full max-w-[1500px] mx-auto px-5 md:px-10 flex flex-col justify-center">
          <FadeUp>
            <p className="text-paper/70 text-[11px] tracking-[0.35em] uppercase mb-6">{c.editorial_label}</p>
            <h2 className="font-serif font-light text-paper text-4xl sm:text-5xl lg:text-7xl tracking-tight max-w-2xl leading-[1.05]">
              {c.editorial_title}
            </h2>
            <p className="mt-6 max-w-md text-paper/80 font-light text-sm md:text-base leading-relaxed">{c.editorial_text}</p>
            <Link
              to={c.editorial_link || "/journal"}
              data-testid="editorial-view-button"
              className="mt-9 inline-flex items-center border border-paper/60 text-paper px-8 py-3.5 text-[11px] font-medium tracking-[0.22em] uppercase transition-all duration-500 hover:bg-paper hover:text-ink"
            >
              View Editorial
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* ---------- ARTISANSHIP ---------- */}
      <section className="py-28 md:py-36 bg-bone" data-testid="artisanship-section">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <FadeUp>
              <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-5">Artisanship</p>
              <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-ink leading-[1.08]">
                Made by Hand.<br /><em className="text-terracotta">Rooted in Place.</em>
              </h2>
              <p className="mt-8 text-base font-light leading-relaxed text-ink/70">
                Every Ipsita piece passes through the hands of Bengal's kantha embroiderers, jamdani weavers, patua painters and terracotta craftsmen. The studio does not imitate craft — it collaborates with the people who carry it.
              </p>
              <PaisleyMotif className="mt-10 w-10 h-10 text-terracotta/50" />
            </FadeUp>
          </div>
          <div className="md:col-span-8 grid grid-cols-2 gap-5">
            {CRAFT_IMAGES.map((src, i) => (
              <FadeUp key={src} delay={i * 0.08} className={i % 2 === 1 ? "mt-10" : ""}>
                <RevealImage src={src} alt="Handmade craft detail" className={i % 2 === 0 ? "aspect-[4/5]" : "aspect-square"} cursor="VIEW" />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- ABOUT PREVIEW ---------- */}
      <section className="py-28 md:py-40" data-testid="about-preview-section">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 relative">
            <RevealImage src={c.about_preview_image} alt="Portrait of Ipsita" className="aspect-[3/4]" cursor="VIEW" />
            <AlpanaCorner className="absolute -bottom-8 -right-4 w-28 h-28 text-alta/40" />
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <FadeUp>
              <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-5">About Ipsita</p>
              <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.06]">
                {c.about_preview_title}
              </h2>
              <p className="mt-8 max-w-lg text-base md:text-lg font-light leading-relaxed text-ink/70">
                {c.about_preview_text}
              </p>
              <Link
                to="/about"
                data-testid="about-read-story-button"
                className="mt-10 inline-flex items-center border border-ink text-ink px-8 py-3.5 text-[11px] font-medium tracking-[0.22em] uppercase transition-all duration-500 hover:bg-ink hover:text-paper"
              >
                Read Her Story
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ---------- WORK WITH IPSITA ---------- */}
      <section className="py-28 md:py-36 bg-ink text-paper relative overflow-hidden grain" data-testid="work-with-section">
        <span className="absolute -top-8 right-6 font-bengali text-[16vw] leading-none text-paper/[0.04] select-none pointer-events-none" aria-hidden="true">রঙ</span>
        <div className="relative max-w-[1500px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <FadeUp>
              <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-5">Collaborations</p>
              <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.06]">
                Create Something Together
              </h2>
              <Link
                to="/work-with-ipsita"
                data-testid="work-start-conversation-button"
                className="mt-10 inline-flex items-center bg-alta text-paper px-8 py-3.5 text-[11px] font-medium tracking-[0.22em] uppercase transition-all duration-500 hover:bg-paper hover:text-ink"
              >
                Start a Conversation
              </Link>
            </FadeUp>
          </div>
          <div className="md:col-span-6">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              {(c.work_categories || FALLBACK.work_categories).map((w, i) => (
                <FadeUp key={w} delay={i * 0.05}>
                  <li className="flex items-center gap-4 py-4 border-b border-paper/15 text-sm tracking-[0.12em] uppercase text-paper/75">
                    <span className="text-alta font-serif italic">{String(i + 1).padStart(2, "0")}</span>
                    {w}
                  </li>
                </FadeUp>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------- FROM THE STUDIO ---------- */}
      {c.studio_images?.length > 0 && (
        <section className="py-24 md:py-32" data-testid="studio-feed-section">
          <div className="max-w-[1500px] mx-auto px-5 md:px-10">
            <FadeUp className="flex items-end justify-between mb-12">
              <div>
                <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-5">From the Studio</p>
                <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink">Work in Progress</h2>
              </div>
              <a href={c.social_instagram || "#"} target="_blank" rel="noreferrer" data-testid="studio-instagram-link" className="link-underline text-[11px] font-medium tracking-[0.25em] uppercase text-ink">
                @ipsita.studio
              </a>
            </FadeUp>
          </div>
          <div className="flex gap-4 overflow-x-auto px-5 md:px-10 pb-4 snap-x" data-testid="studio-feed-row">
            {c.studio_images.map((src, i) => (
              <div key={i} className="shrink-0 w-56 md:w-72 snap-start" data-cursor="VIEW">
                <RevealImage src={src} alt="Studio process" className="aspect-[4/5]" delay={i * 0.05} />
              </div>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
